import { Form, Spin, Checkbox, Alert } from 'antd';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { familyService } from 'services';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { CHILD_PROVIDER_SITUATION_VALUES, FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { Banner } from 'components/ui/Banner';
import { DatePicker } from 'components/ui/DatePicker';
import { getFirstName } from 'utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationChildrenChildCareNeeds({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);
  const location = useLocation();

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const careStartDateUnknownFormValue = Form.useWatch('careStartDateUnknown', form);
  const careStartDateFormValue = Form.useWatch('careStartDate', form);

  const urlParams = new URLSearchParams(location.search);
  const currentChildIndex = parseInt(urlParams.get('entity') || '0', 10);

  const currentChild = application?.children?.[currentChildIndex];
  const childFirstName = getFirstName({ firstName: currentChild?.firstName });
  const isReferralNeeded = currentChild?.providerSituation === CHILD_PROVIDER_SITUATION_VALUES.ReferralNeeded;

  useEffect(() => {
    if (currentChild) {
      form.setFieldsValue({
        careStartDate: currentChild.careStartDateUnknown ? null : currentChild.careStartDate,
        careStartDateUnknown: currentChild.careStartDateUnknown,
      });
    }
  }, [currentChild, form]);

  const handleSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        const nextStep = getNextStep(
          FAMILY_APPLICATION_STEPS.CHILDREN_CHILD_CARE_NEEDS,
          _newApplication,
          stepConfig,
          currentChildIndex,
        );
        setStep({ applicationId: _newApplication.id, newStep: nextStep });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const savedChild = await familyService.addUpdateFamilyChild(household?.id, currentChild?.id || null, {
          ...currentChild,
          careStartDate: values.careStartDate,
          careStartDateUnknown: values.careStartDateUnknown,
        });

        const updatedChildren = [...(application.children || [])];
        updatedChildren[currentChildIndex] = savedChild;
        const updatedApplication = { ...application, children: updatedChildren };

        const finalApplication = await familyService.saveApplication(updatedApplication);
        setApplication(finalApplication);

        _goToNextStep(finalApplication);
      } catch (error) {
        console.error('Error saving child care needs:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, currentChild, currentChildIndex, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title={`${childFirstName} - Child Care Needs`} />

      <Form form={form} onFinish={handleSubmit} layout="vertical" disabled={readOnly}>
        <div className="p-7 pt-0">
          <div className="space-y-6">
            <Banner
              question={`When do you expect ${childFirstName} to start care?`}
              guidance="An estimate is fine — you can always update this later"
              className="my-12"
            />

            <div>
              <Form.Item
                name="careStartDate"
                label="Start date"
                rules={[
                  {
                    required: isReferralNeeded || !careStartDateUnknownFormValue,
                    message: isReferralNeeded
                      ? 'Please select a start date'
                      : "Please select a start date or check that you don't have a start date yet",
                  },
                ]}
                className="mb-0"
              >
                <DatePicker
                  placeholder="Select start date"
                  disabled={careStartDateUnknownFormValue}
                  data-testid="care-start-date"
                />
              </Form.Item>
            </div>

            {isReferralNeeded &&
              careStartDateFormValue &&
              dayjs(careStartDateFormValue).isValid() &&
              dayjs(careStartDateFormValue).isAfter(dayjs().add(90, 'day')) && (
                <Alert
                  type="warning"
                  showIcon
                  message="Your care start date is more than 90 days away"
                  description="We may not be able to confirm provider availability this far in advance. You can still continue, and your team will follow up when timing allows."
                />
              )}

            {!isReferralNeeded && (
              <Form.Item name="careStartDateUnknown" valuePropName="checked">
                <Checkbox
                  onChange={(e) => {
                    if (e.target.checked) {
                      form.setFieldsValue({ careStartDate: null });
                    }
                  }}
                  data-testid="care-start-date-unknown"
                >
                  I don't have a start date yet
                </Checkbox>
              </Form.Item>
            )}
          </div>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CHILDREN_CHILD_CARE_NEEDS,
                application,
                stepConfig,
                currentChildIndex,
              );
              setStep({ applicationId: application?.id, newStep: previousStep });
            },
            disabled: submitting,
            'data-testid': 'children-child-care-needs-back-button',
          }}
          nextButtonProps={{
            loading: submitting,
            'data-testid': 'children-child-care-needs-next-button',
          }}
        />
      </Form>
    </Spin>
  );
}
